import {
    BalanceChanges,
    resolveAddressLookups,
    simulateTransactionAndGetBalanceChanges,
    TransactionsWithDataGenerateReturn,
    AnalyticsInfo,
    fetchAndDeserializeVersionedTransactionsWithLoanInfo
} from "@bridgesplit/utils";
import { PublicKey, MessageAccountKeys, VersionedTransaction } from "@solana/web3.js";

import { AbfTransactionHeaders, IncreaseLeverageTransactionInput, SdkApiLoanInfo, TXN_FLASH_BORROW } from "../types";
import { createMarketsInstance } from "./auth";

export async function getLoopWindTransaction(
    authentication: AbfTransactionHeaders,
    arg: IncreaseLeverageTransactionInput
): Promise<TransactionsWithDataGenerateReturn<{ loanAddress: string }>> {
    const api = createMarketsInstance(authentication);

    const data = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        IncreaseLeverageTransactionInput,
        SdkApiLoanInfo
    >(api, TXN_FLASH_BORROW, arg);

    return {
        transactions: data.transactions,
        loanAddress: data.expectedLoanInfo.loan.address
    };
}

export async function getLoopWindTransactionAndBalanceChanges(
    authentication: AbfTransactionHeaders,
    arg: IncreaseLeverageTransactionInput,
    queryParams?: object,
    analyticsInfo?: AnalyticsInfo
): Promise<
    TransactionsWithDataGenerateReturn<{
        loanAddress: string;
        balanceChanges: BalanceChanges;
        simulationLogs?: string[];
    }>
> {
    const { transactions, loanAddress } = await getLoopWindTransaction(authentication, arg);
    // Use the final transaction as the best proxy for the wind output.
    const transaction = transactions[transactions.length - 1];

    if (!transaction || !loanAddress || !(transaction instanceof VersionedTransaction)) {
        return { transactions, loanAddress, balanceChanges: {} };
    }

    // Bundled flows (e.g., oracle crank prepended) cannot be simulated standalone —
    // the user tx depends on state that only lands after the preceding tx.
    if (transactions.length > 1) {
        return { transactions, loanAddress, balanceChanges: {} };
    }

    const accountsToTrack: PublicKey[] = [];
    const lutAccounts = await resolveAddressLookups(transaction);
    const accountKeys: MessageAccountKeys = transaction.message.getAccountKeys({
        addressLookupTableAccounts: lutAccounts
    });
    const staticAccountsToTrack: PublicKey[] = accountKeys.staticAccountKeys.filter(
        (_, idx) => transaction.message.isAccountWritable(idx) || transaction.message.isAccountSigner(idx)
    );

    if (staticAccountsToTrack.length > 0) {
        accountsToTrack.push(...staticAccountsToTrack);
    }

    if (accountKeys.accountKeysFromLookups) {
        accountsToTrack.push(...accountKeys.accountKeysFromLookups.writable);
    }

    const { balanceChanges, simulationLogs } = await simulateTransactionAndGetBalanceChanges({
        transaction,
        user: authentication.userWallet ? new PublicKey(authentication.userWallet) : PublicKey.default,
        accountsToTrack,
        analyticsInfo: {
            ...analyticsInfo,
            loanAddress
        }
    });

    return { transactions, loanAddress, balanceChanges, simulationLogs };
}
